import { atom, selectorFamily } from 'recoil';
import { incrementOnStudioReset, incrementOnWorkspaceConfigurationChange } from '../effects/signalEffects';
import { blockSettings } from './configuration/blockSettings';
import { portfolioStrategyFilterType } from './configuration/customViewOptions';
import type { BlockId } from './types';

/**
 * An id that is added to the query key of studio analysis queries
 * so that it's cache will be invalidated when this atom changes
 */
export const analysisIdState = atom<number>({
  key: 'analysisIdState',
  default: 0,
  effects: [incrementOnStudioReset, incrementOnWorkspaceConfigurationChange],
});

/**
 * This selector determines whether we should pass groupRootLevelInvestments flag
 * when making analysis request for this block
 *
 * Grouping root level investments means that if there are any investments at the top level
 * (direct children of the root) then these investments will be moved into "Uncategorized" strategy on backend
 */
export const shouldGroupRootLevelInvestments = selectorFamily<boolean, BlockId>({
  key: 'shouldGroupRootLevelInvestments',
  get:
    (blockId: BlockId) =>
    ({ get }) => {
      const blockType = get(blockSettings(blockId)).customBlockType;

      // For portfolio contribution block check if either all strategies or top level strategies option is selected
      if (blockType === 'PORTFOLIO_CONTRIBUTION') {
        const portfolioStrategyFilter = get(portfolioStrategyFilterType(blockId));
        return portfolioStrategyFilter === 'ALL_STRATEGIES' || portfolioStrategyFilter === 'TOP_LEVEL_STRATEGIES';
      }

      // Historical allocations timeseries block should always group root level investments
      if (blockType === 'HISTORICAL_ALLOCATIONS_TIMESERIES') {
        return true;
      }

      // For all other blocks, do not group root level investments
      return false;
    },
});
